import { useState } from 'react';
import { Group, Badge, Table, Select, Button, Modal, Text } from '@mantine/core';
import { IconCrown, IconUsersGroup, IconPlus } from '@tabler/icons-react';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { SectionCard } from '../components/SectionCard';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { formatDate, formatPhone } from '../lib/format';
import { genderRoleLabel } from '../lib/roleLabel';
import { PREMIUM_UI_ENABLED } from '../lib/features';
import { QuickParentForm } from '../components/QuickCreate';

interface ParentRow {
  id: string; fullName: string; phone?: string | null; gender?: 'MALE' | 'FEMALE' | null; premiumUntil?: string | null; isActive: boolean;
  _count: { children: number; giftsSent: number; giftsReceived: number };
}

const active = (until?: string | null) => !!until && new Date(until) > new Date();

/**
 * Ota-onalar — qidiruv, premium filtri, saralash. Premium grant superadminda.
 *
 * PREMIUM MUZLATILGAN (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`): premium
 * filtri, saralashi, toj (crown) nishoni, obuna va sovg'a ustunlari YASHIRILADI —
 * hozir barcha ota-onaga 2027-yilgacha premium berilgan, ya'ni ustun hech qanday
 * ma'lumot bermay, faqat chalg'itardi. Kod o'chirilmagan: bayroq qaytarilsa,
 * hammasi joyida.
 */
export default function Parents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [premiumFilter, setPremiumFilter] = useState<string>('');
  const [sort, setSort] = useState('fullName:asc');
  const [createOpen, createH] = useDisclosure(false);

  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    q: q || undefined,
    premium: premiumFilter || undefined,
    sort,
  };

  const parents = usePagedQuery<ParentRow>('parents-page', '/premium/parents', filtrlar);

  return (
    <>
      <PageHeader
        title={t('nav.parents')}
        subtitle={`${t('common.total')}: ${parents.total}`}
        action={<Button leftSection={<IconPlus size={16} />} onClick={createH.open}>{t('parentsPage.add')}</Button>}
      />
      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); parents.setPage(1); }}
        searchPlaceholder={t('parentsPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); parents.setPage(1); }}
        sortOptions={[
          { value: 'fullName:asc', label: t('studentsPage.sortNameAsc') },
          { value: 'fullName:desc', label: t('studentsPage.sortNameDesc') },
          ...(PREMIUM_UI_ENABLED ? [{ value: 'premiumUntil:desc', label: t('parentsPage.sortPremium') }] : []),
          { value: 'createdAt:desc', label: t('studentsPage.sortNewest') },
        ]}
      >
        {PREMIUM_UI_ENABLED && (
          <Select
            placeholder={t('parentDetail.subscription')}
            data={[{ value: '', label: t('teachersPage.allRoles') }, { value: 'yes', label: t('nav.premium') }, { value: 'no', label: t('parentsPage.regular') }]}
            value={premiumFilter}
            onChange={(v) => { setPremiumFilter(v ?? ''); parents.setPage(1); }}
            w={140}
            clearable
          />
        )}
        <TableExport<ParentRow>
          endpoint="/premium/parents"
          params={filtrlar}
          filename={t('nav.parents')}
          ustunlar={[
            { key: 'fullName', label: t('roles.PARENT') },
            { key: 'gender', label: t('parentDetail.gender') },
            { key: 'phone', label: t('fields.phone') },
            { key: 'children', label: t('parentDetail.children'), get: (r) => r._count.children },
          ]}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconUsersGroup} p={0} pos="relative">
        <FetchOverlay visible={parents.fetching} />
        {/* Ikkita ustun yashirilganda minWidth ham kichrayadi — bo'sh joy/keraksiz gorizontal skroll qolmasin */}
        <Table.ScrollContainer minWidth={PREMIUM_UI_ENABLED ? 760 : 560}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('roles.PARENT')}</Table.Th>
                <Table.Th>{t('parentDetail.gender')}</Table.Th>
                <Table.Th>{t('fields.phone')}</Table.Th>
                <Table.Th>{t('parentDetail.children')}</Table.Th>
                {/* Sovg'a va obuna ustunlari — faqat premium yoqilganda (jadval kengligi ham shunga qarab) */}
                {PREMIUM_UI_ENABLED && <Table.Th>{t('parentsPage.giftsSentReceived')}</Table.Th>}
                {PREMIUM_UI_ENABLED && <Table.Th>{t('parentDetail.subscription')}</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {parents.items.map((p) => (
                <Table.Tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/parents/${p.id}`)}>
                  <Table.Td fw={500}>
                    <Group gap={6}>{p.fullName}{PREMIUM_UI_ENABLED && active(p.premiumUntil) && <IconCrown size={15} color="var(--mantine-color-yellow-6)" />}</Group>
                  </Table.Td>
                  <Table.Td>
                    {(() => { const r = genderRoleLabel('PARENT', p.gender); return r ? <Badge variant="light" color={r.color}>{r.label}</Badge> : '—'; })()}
                  </Table.Td>
                  <Table.Td>{formatPhone(p.phone)}</Table.Td>
                  <Table.Td>{p._count.children}</Table.Td>
                  {PREMIUM_UI_ENABLED && <Table.Td>{p._count.giftsSent} / {p._count.giftsReceived}</Table.Td>}
                  {PREMIUM_UI_ENABLED && (
                    <Table.Td>
                      {active(p.premiumUntil)
                        ? <Badge color="yellow" variant="light">{formatDate(p.premiumUntil!)} {t('parentDetail.until')}</Badge>
                        : <Badge color="gray" variant="light">{t('parentsPage.regular')}</Badge>}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </SectionCard>
      {/* Yaratish — o'quvchi kartochkasidagi AYNI forma (`QuickParentForm`), ya'ni
          bitta endpoint (`POST /students/parents`) va bitta validatsiya to'plami.
          Yaratilgach kartochkaga o'tiladi: ota-onaga farzand biriktirish o'sha yerda. */}
      <Modal opened={createOpen} onClose={createH.close} title={t('parentsPage.add')}>
        <Text size="sm" c="dimmed" mb="sm">{t('parentsPage.addHint')}</Text>
        <QuickParentForm onCreated={(newId) => { createH.close(); parents.refetch(); navigate(`/parents/${newId}`); }} />
      </Modal>

      <TablePagination
        page={parents.page}
        pages={parents.pages}
        total={parents.total}
        limit={parents.limit}
        onPage={parents.setPage}
        onLimit={parents.setLimit}
      />
    </>
  );
}
